import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Users } from "lucide-react";
import api from "@/lib/api";
import { setSession } from "@/lib/auth";

/**
 * TeamAccept.jsx
 * ------------------------------------------------------------------
 * Route: /team/accept?token=...
 * Hit by the "Accept invite" button/link in the invite email.
 * Calls POST /team/accept-callback -> { token, business_id, member }
 * On success, stores the member's session the same way merchant login
 * does, then redirects into the dashboard.
 * ------------------------------------------------------------------
 */

const ICON_BY_STATUS = {
  loading: { Icon: Users, bg: "bg-lilac" },
  success: { Icon: CheckCircle2, bg: "bg-mint" },
  error: { Icon: XCircle, bg: "bg-peach" },
};

export default function TeamAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [errorInfo, setErrorInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorInfo({
        title: "Invite link is incomplete",
        message: "This link is missing its token. Please use the link from your invite email directly.",
      });
      return;
    }

    api
      .post("/team/accept-callback", { token })
      .then(({ data }) => {
        setSession(data.token, data.member?.name || data.member?.email || "");
        setStatus("success");
        setTimeout(() => navigate("/dashboard"), 1200);
      })
      .catch((e) => {
        const err = e?.response?.data?.error;
        setStatus("error");
        if (err === "invite_invalid_or_expired") {
          setErrorInfo({
            title: "This invite has expired",
            message: "Invite links are valid for 48 hours. Ask the team admin to send you a new invite.",
          });
        } else if (err === "seats_full") {
          setErrorInfo({
            title: "All seats are taken",
            message: "The team is at its seat limit right now. Ask the admin to add another seat, then try this link again.",
          });
        } else {
          setErrorInfo({
            title: "Couldn't accept this invite",
            message: "Something went wrong on our end. Please try the link again, or ask the admin to resend it.",
          });
        }
      });
  }, [token, navigate]);

  const { Icon, bg } = ICON_BY_STATUS[status];

  return (
    <section className="grid min-h-screen place-items-center bg-cream px-6">
      <div className="w-full max-w-md rounded-3xl border border-ink/10 bg-white p-8 text-center shadow-sm">
        <div className={`mx-auto grid h-14 w-14 place-items-center rounded-2xl ${bg} text-ink`}>
          <Icon className="h-6 w-6" />
        </div>

        {status === "loading" && (
          <>
            <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">
              Joining the team…
            </h1>
            <p className="mt-2 text-sm text-ink/60">Verifying your invite, one second.</p>
            <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-ink/40" />
          </>
        )}

        {status === "success" && (
          <>
            <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">You're in!</h1>
            <p className="mt-2 text-sm text-ink/60">Taking you to the dashboard…</p>
            <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-ink/40" />
          </>
        )}

        {status === "error" && errorInfo && (
          <>
            <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">{errorInfo.title}</h1>
            <p className="mt-2 text-sm text-ink/60">{errorInfo.message}</p>
            <Link
              to="/team/login"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
            >
              Already accepted before? Log in
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
